// ════════════════════════════════════════════════════════════════════════════
//  ManDana — a reference never travels before its blob
//  ---------------------------------------------------------------------------
//  THE ORDER IS THE WHOLE STORY. Bytes go to the store, then the document that
//  names them goes to the server. Get it the other way round and the other
//  device pulls a desk whose image is a name pointing at nothing: a broken
//  picture with a perfectly valid reference under it, and no way to tell
//  whether the bytes are coming or gone.
//
//  This is NOT what mdPendingKeys covers. That one keeps a document that is
//  mid-upload from being MOURNED — read as a deletion because it dropped out of
//  the offer. This one keeps a document from being SENT before its bytes are
//  acknowledged. Opposite directions, both needed.
//
//  ── A DOCUMENT DECLARES ITS BYTES ─────────────────────────────────────────
//  The walk looks for references MARKED as blobs — `{ hash, blob: 1 }` — and
//  not for anything that happens to be 64 hex characters under a field called
//  `hash`. Guessing has exactly one failure mode and it is silent: a false
//  positive holds a document back for ever, and nobody goes looking for a
//  document that simply never syncs. md-file.ts's own id is a hash; so is a
//  board element id in some builds.
//
//  So the obligation sits with the family: if you reference bytes, say so. And
//  because a family can forget — that is what 0.6's four-changes story is about
//  — mdBarrierUnmarked names hash-shaped fields carrying no marker, so
//  forgetting is something a test catches rather than something a person
//  discovers on their second device.
//
//  ── IT FAILS OPEN, DELIBERATELY ───────────────────────────────────────────
//  Asked what has landed and given no usable answer, it holds nothing. A
//  barrier that failed closed would stop every document syncing the moment the
//  blob layer could not answer — and the blob store is optional in a way the
//  document path is not. Failing open costs one broken picture on another
//  device for as long as an upload takes. Failing closed costs the account's
//  writing.
//
//  ── PURE ──────────────────────────────────────────────────────────────────
//  No store, no transport, no clock of its own. The loop's release gate is the
//  model: the rules that decide whether something may be published are worth
//  proving without a repository, a network or a device.
// ════════════════════════════════════════════════════════════════════════════

/** A hold recorded when bytes were accepted, before the document naming them. */
interface MdBarrierHold {
  hash: string;
  at: number;
}

(function (g: any) {
  'use strict';

  /* Lowercase hex, matching md-transport.ts and server/blobs.js. One spelling,
     or the same bytes have two names. */
  const HASH = /^[0-9a-f]{64}$/;

  /* Deep enough for a board scene with nested element groups and far short of
     a stack overflow. A structure deeper than this is not a document anybody
     wrote; it is a cycle the visited set somehow missed. */
  const MAX_DEPTH = 64;

  const isHash = (value: unknown): value is string =>
    typeof value === 'string' && HASH.test(value);

  /** Every blob hash a value DECLARES, once each, in reading order.

      Reading order rather than sorted, because the first one missing is the
      one worth naming to a person — and a stable order makes two devices agree
      about which that is. */
  function mdBarrierHashes(value: unknown): string[] {
    return walk(value, true);
  }

  /** Hash-shaped fields carrying no marker: what a family forgot to declare.

      This is an AUDIT, never a barrier input. Holding a document back for one
      of these is precisely the silent failure the marker exists to avoid; the
      answer to an undeclared hash is a red test in the family that wrote it. */
  function mdBarrierUnmarked(value: unknown): string[] {
    return walk(value, false);
  }

  function walk(value: unknown, marked: boolean): string[] {
    const out: string[] = [];
    const seen = new Set<unknown>();
    const take = (node: any, depth: number) => {
      if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return;
      if (seen.has(node)) return;
      seen.add(node);
      if (Array.isArray(node)) {
        for (const item of node) take(item, depth + 1);
        return;
      }
      if (isHash(node.hash) && !!node.blob === marked && !out.includes(node.hash)) {
        out.push(node.hash);
      }
      for (const held of Object.values(node)) take(held, depth + 1);
    };
    take(value, 0);
    return out;
  }

  /** Which of these documents must NOT be pushed yet.

      `landed` is a Set of acknowledged hashes or a predicate — the caller owns
      that answer, and a barrier that reached for it would be a pure function
      with a store in it. Anything else means "I cannot say", and nothing is
      held; see the header. */
  function mdBarrierHold(docs: unknown, landed: unknown): string[] {
    if (!docs || typeof docs !== 'object') return [];
    const has = typeof landed === 'function' ? landed as (h: string) => boolean
      : (landed instanceof Set ? (h: string) => landed.has(h) : null);
    if (!has) return [];
    const out: string[] = [];
    for (const [key, value] of Object.entries(docs as Record<string, unknown>)) {
      const wanted = mdBarrierHashes(value);
      if (wanted.some((hash) => !has(hash))) out.push(key);
    }
    return out;
  }

  /* Long enough that no document write is still in flight, short enough that a
     failed one does not sit on somebody's allowance for a week. A hold minted
     seconds ago is a document about to be written, not an orphan — and getting
     that wrong releases bytes a record is about to name. */
  const MD_BARRIER_ORPHAN_GRACE_MS = 24 * 60 * 60 * 1000;

  /** Holds nothing points at any more, and old enough to be sure of it.

      THE DANGEROUS DIRECTION IS DOWN. An incomplete answer to "what is
      referenced?" reads as "nothing references these" and releases the
      account's files, so an unusable answer releases nothing at all. */
  function mdBarrierOrphans(holds: unknown, named: unknown, now: unknown,
    graceMs?: unknown): string[] {
    if (!Array.isArray(holds)) return [];
    const has = typeof named === 'function' ? named as (h: string) => boolean
      : (named instanceof Set ? (h: string) => named.has(h) : null);
    if (!has) return [];
    const clock = typeof now === 'number' && Number.isFinite(now) ? now : Date.now();
    const grace = typeof graceMs === 'number' && Number.isFinite(graceMs) && graceMs >= 0
      ? graceMs : MD_BARRIER_ORPHAN_GRACE_MS;
    const out: string[] = [];
    for (const held of holds as MdBarrierHold[]) {
      if (!held || !isHash(held.hash)) continue;
      if (!Number.isFinite(held.at) || clock - held.at < grace) continue;
      if (!has(held.hash) && !out.includes(held.hash)) out.push(held.hash);
    }
    return out;
  }

  Object.assign(g, {
    MD_BARRIER_ORPHAN_GRACE_MS,
    mdBarrierHashes, mdBarrierUnmarked, mdBarrierHold, mdBarrierOrphans,
  });

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = {
      MD_BARRIER_ORPHAN_GRACE_MS,
      mdBarrierHashes, mdBarrierUnmarked, mdBarrierHold, mdBarrierOrphans,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
